import React, { useState, useEffect, useRef } from "react";
import logo from "../Assets/logo.png";

export default function Chatbot() {
  const [open, setOpen] = useState(false); // Controls chat window visibility
  const [showChatInterface, setShowChatInterface] = useState(false); // Controls chat interface visibility
  const [message, setMessage] = useState("");
  const [chatLog, setChatLog] = useState([]); // Chat history
  const [error, setError] = useState(null); // Error state
  const [isLoading, setIsLoading] = useState(false); // Loading state
  const messagesEndRef = useRef(null); // Ref for scrolling

  // API URL from environment variable or fallback to local
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  // Scroll to bottom when chatLog or error changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatLog, error]);

  // Show chat interface and initialize with welcome message
  const showChatInterfaceHandler = () => {
    setShowChatInterface(true);
    setChatLog([{ sender: "bot", text: "Hello! How can I assist you today?" }]);
  };

  // Send message to server
  const sendMessage = async () => {
    if (!message.trim()) return; // Prevent sending empty messages

    // Add user message to chat log
    setChatLog((prev) => [...prev, { sender: "user", text: message }]);
    setError(null);
    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message }),
      });

      if (!res.ok) {
        throw new Error(`Server error: ${res.status}`);
      }

      const data = await res.json();
      if (data.error) {
        throw new Error(data.error);
      }

      setChatLog((prev) => [...prev, { sender: "bot", text: data.response }]);
    } catch (err) {
      console.error("Error:", err);
      setError(err.message);
      setChatLog((prev) => [...prev, { sender: "bot", text: `Error: ${err.message}` }]);
    } finally {
      setIsLoading(false);
      setMessage("");
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !isLoading) {
      sendMessage();
    }
  };

  return (
    <div>
      {/* Floating Button */}
      <button
        style={floatingButtonStyle}
        onClick={() => setOpen(true)}
        aria-label="Open chatbot"
      >
        💬
      </button>

      {/* Chat Window */}
      {open && (
        <div style={chatWindowStyle} role="dialog" aria-labelledby="chatbot-title">
          {/* Header */}
          <div style={headerStyle}>
            <div style={headerPattern}></div>
            <img src={logo} alt="InaBot Logo" style={logoStyle} />
            <h3 id="chatbot-title" style={headerTitleStyle}>
              InaBot
            </h3>
            <p style={headerSubtitleStyle}>We are here to help you!</p>
          </div>

          {/* Chat Button */}
          {!showChatInterface && (
            <div style={chatButtonContainerStyle}>
              <button
                style={chatButtonStyle}
                onClick={showChatInterfaceHandler}
                aria-label="Start chatting"
              >
                <span style={chatIconStyle}>💬</span> Chat with us now
              </button>
            </div>
          )}

          {/* Messages Container */}
          {showChatInterface && (
            <div style={messagesContainerStyle} aria-live="polite">
              {chatLog.map((msg, index) => (
                <div
                  key={index}
                  style={
                    msg.sender === "user" ? userMessageStyle : botMessageStyle
                  }
                >
                  {msg.text}
                </div>
              ))}
              {error && (
                <div style={{ ...botMessageStyle, color: "red" }}>
                  Error: {error}
                </div>
              )}
              {isLoading && (
                <div style={botMessageStyle}>Typing...</div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Input Container */}
          {showChatInterface && (
            <div style={inputContainerStyle}>
              <input
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                style={inputStyle}
                placeholder="Type your message..."
                aria-label="Chat message input"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                style={{
                  ...sendButtonStyle,
                  opacity: message.trim() && !isLoading ? 1 : 0.5,
                  cursor: message.trim() && !isLoading ? "pointer" : "not-allowed",
                }}
                disabled={!message.trim() || isLoading}
                aria-label="Send message"
              >
                {isLoading ? "Sending..." : "Send"}
              </button>
            </div>
          )}

          {/* Floating Close Button */}
          <button
            style={closeButtonStyle}
            onClick={() => setOpen(false)}
            aria-label="Close chatbot"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

// Styles (updated for better UX)
const floatingButtonStyle = {
  position: "fixed",
  bottom: "20px",
  right: "20px",
  background: "linear-gradient(45deg, #4CAF50, rgb(30, 103, 34))",
  color: "white",
  border: "none",
  fontSize: "24px",
  padding: "15px",
  borderRadius: "50%",
  cursor: "pointer",
  boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
  zIndex: 1000,
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: "0 5px 15px rgba(76, 175, 80, 0.5)",
  },
};

const chatWindowStyle = {
  position: "fixed",
  bottom: "80px",
  right: "20px",
  width: "320px",
  background: "#E8F5E9",
  borderRadius: "25px",
  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  zIndex: 1000,
  border: "2px solid #4CAF50",
};

const headerStyle = {
  padding: "20px 15px",
  background: "linear-gradient(45deg, #4CAF50, rgb(30, 103, 34))",
  color: "white",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
  position: "relative",
  overflow: "hidden",
};

const logoStyle = {
  width: "50px",
  height: "50px",
  borderRadius: "50%",
  objectFit: "cover",
  margin: "10px 0",
  padding: "5px",
  backgroundColor: "#ffffff",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  border: "2px solid #ffffff",
};

const headerTitleStyle = {
  margin: "8px 0 5px 0",
  fontSize: "22px",
  fontWeight: "bold",
  textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
};

const headerSubtitleStyle = {
  fontSize: "14px",
  margin: "0",
  opacity: "0.9",
  textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
};

// Add a decorative background pattern to the header
const headerPattern = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "linear-gradient(135deg, rgba(255,255,255,0.1) 25%, transparent 25%) -10px 0, linear-gradient(225deg, rgba(255,255,255,0.1) 25%, transparent 25%) -10px 0",
  backgroundSize: "20px 20px",
  opacity: "0.4",
};

const chatButtonContainerStyle = {
  padding: "15px",
  display: "flex",
  justifyContent: "center",
};

const chatButtonStyle = {
  background: "linear-gradient(45deg, #4CAF50, rgb(30, 103, 34))",
  color: "white",
  border: "2px solid white",
  padding: "12px 24px",
  borderRadius: "20px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  fontSize: "16px",
  transition: "all 0.3s ease",
  boxShadow: "0 2px 8px rgba(76, 175, 80, 0.2)",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: "0 5px 15px rgba(76, 175, 80, 0.5)",
  },
};

const chatIconStyle = {
  marginRight: "8px",
};

const messagesContainerStyle = {
  flex: 1,
  padding: "15px",
  maxHeight: "350px",
  overflowY: "auto",
  backgroundColor: "#F9FAFB",
};

const userMessageStyle = {
  textAlign: "right",
  marginBottom: "12px",
  color: "#fff",
  background: "linear-gradient(45deg, #4CAF50, rgb(30, 103, 34))",
  padding: "12px 16px",
  borderRadius: "18px 18px 0 18px",
  maxWidth: "80%",
  marginLeft: "auto",
  wordBreak: "break-word",
  boxShadow: "0 2px 4px rgba(76, 175, 80, 0.2)",
  fontSize: "14px",
  lineHeight: "1.5",
  position: "relative",
  animation: "slideIn 0.3s ease",
};

const botMessageStyle = {
  textAlign: "left",
  marginBottom: "12px",
  color: "#2E7D32",
  backgroundColor: "#fff",
  padding: "12px 16px",
  borderRadius: "18px 18px 18px 0",
  maxWidth: "80%",
  marginRight: "auto",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
  wordBreak: "break-word",
  fontSize: "14px",
  lineHeight: "1.5",
  border: "1px solid #4CAF50",
  position: "relative",
  animation: "slideIn 0.3s ease",
};

const inputContainerStyle = {
  display: "flex",
  borderTop: "1px solid #4CAF50",
  padding: "12px 16px",
  backgroundColor: "#fff",
};

const inputStyle = {
  flex: 1,
  border: "1px solid #E5E7EB",
  padding: "12px",
  outline: "none",
  borderRadius: "12px",
  backgroundColor: "#F9FAFB",
  fontSize: "14px",
  transition: "all 0.2s ease",
  "&:focus": {
    borderColor: "#4CAF50",
    boxShadow: "0 0 0 2px rgba(76, 175, 80, 0.1)",
  },
};

const sendButtonStyle = {
  background: "linear-gradient(45deg, #4CAF50, rgb(30, 103, 34))",
  color: "white",
  border: "none",
  padding: "10px 20px",
  marginLeft: "10px",
  borderRadius: "12px",
  fontSize: "14px",
  fontWeight: "500",
  transition: "all 0.3s ease",
  boxShadow: "0 2px 4px rgba(76, 175, 80, 0.1)",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: "0 5px 15px rgba(76, 175, 80, 0.5)",
  },
  "&:disabled": {
    backgroundColor: "#E5E7EB",
    cursor: "not-allowed",
    transform: "none",
  },
};

const closeButtonStyle = {
  position: "absolute",
  top: "10px",
  left: "10px",
  background: "rgba(255, 255, 255, 0.1)",
  border: "none",
  color: "#fff",
  fontSize: "20px",
  cursor: "pointer",
  fontWeight: "bold",
  width: "30px",
  height: "30px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background 0.2s ease",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.2)",
  },
};